/**
 * Capacitor-only helpers. All layout/chrome gated on isNativeApp() so the
 * browser site is unchanged.
 */

export function isNativeApp() {
  if (typeof window === 'undefined') return false;
  return !!(
    window.Capacitor &&
    window.Capacitor.isNativePlatform &&
    window.Capacitor.isNativePlatform()
  );
}

export function getNativePlatform() {
  if (!isNativeApp()) return null;
  try {
    if (window.Capacitor.getPlatform) return window.Capacitor.getPlatform();
  } catch {
    /* ignore */
  }
  return null;
}

export function dashboardRoute(user) {
  if (user?.role === 'admin') return '/admin-dashboard';
  if (user?.role === 'producer') return '/producer-dashboard';
  if (user?.role === 'fan') return '/fan-dashboard';
  if (user) return '/investor-dashboard';
  return '/login';
}

/** Tab roots: no back chevron; Android back minimizes. */
export const NATIVE_TAB_ROOTS = new Set([
  '/',
  '/classic',
  '/themes/hotstar',
  '/marketplace',
  '/community',
  '/fraternity',
  '/login',
  '/fan-dashboard',
  '/investor-dashboard',
  '/producer-dashboard',
  '/admin-dashboard',
]);

export function isNativeTabRoot(pathname) {
  if (!pathname) return true;
  if (NATIVE_TAB_ROOTS.has(pathname)) return true;
  return false;
}

export function nativeTitleForPath(pathname) {
  if (!pathname) return 'Fanzingy';
  if (pathname === '/' || pathname === '/classic' || pathname.startsWith('/themes')) return 'Fanzingy';
  if (pathname.startsWith('/marketplace')) return 'Browse';
  if (pathname.startsWith('/movie/') && pathname.includes('/crew')) return 'Crew';
  if (pathname.startsWith('/movie/') && pathname.includes('/funding')) return 'Funding';
  if (pathname.startsWith('/movie/')) return 'Movie';
  if (pathname.startsWith('/community')) return 'Community';
  if (pathname.startsWith('/fraternity/inbox')) return 'Inbox';
  if (pathname.startsWith('/fraternity/me')) return 'Profile';
  if (pathname.startsWith('/fraternity')) return 'Fraternity';
  if (pathname.startsWith('/fan-dashboard')) return 'Fan';
  if (pathname.startsWith('/investor-dashboard')) return 'Investor';
  if (pathname.startsWith('/producer-dashboard')) return 'Producer';
  if (pathname.startsWith('/admin-dashboard')) return 'Admin';
  if (pathname.startsWith('/producer/track-record')) return 'Track record';
  if (pathname.startsWith('/producer/')) return 'Producer';
  if (pathname.startsWith('/invite')) return 'Invite';
  if (pathname.startsWith('/notifications')) return 'Alerts';
  if (pathname.startsWith('/legal')) return 'Legal';
  if (pathname.startsWith('/login')) return 'Sign in';
  if (pathname.startsWith('/register')) return 'Join';
  if (pathname.startsWith('/forgot-password')) return 'Reset';
  if (pathname.startsWith('/reset-password')) return 'Reset';
  if (pathname.startsWith('/payment')) return 'Payment';
  return 'Fanzingy';
}

function applyDocumentClasses() {
  if (typeof document === 'undefined' || !isNativeApp()) return;
  const root = document.documentElement;
  root.classList.add('native-app');
  const platform = getNativePlatform();
  if (platform === 'ios') {
    root.classList.add('native-ios');
    root.classList.remove('native-android');
  } else if (platform === 'android') {
    root.classList.add('native-android');
    root.classList.remove('native-ios');
  }
}

/* ---------- Page title override (e.g. movie name on /movie/:id) ---------- */

/** Pages call this once their data loads; NativeTopBar shows it for that path. */
export function setNativeTitle(title) {
  if (typeof window === 'undefined') return;
  window.__fzNativeTitle = { path: window.location.pathname, title };
  window.dispatchEvent(new Event('fz-native-title'));
}

export function getNativeTitleOverride(pathname) {
  const o = typeof window !== 'undefined' && window.__fzNativeTitle;
  return o && o.path === pathname ? o.title : null;
}

/* ---------- Haptics (no-op on the web) ---------- */

export async function hapticTap() {
  if (!isNativeApp()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {
    /* plugin unavailable */
  }
}

export async function hapticSuccess() {
  if (!isNativeApp()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Success });
  } catch {
    /* plugin unavailable */
  }
}

/* ---------- Safe-area insets ----------
 * Android WebView reports env(safe-area-inset-*) as 0, so MainActivity pushes the
 * real system-bar insets (CSS px) as window.__FZ_INSETS__ + an 'fz-insets' event.
 * iOS keeps using env() from native.css. */
const INSETS_KEY = 'fz_native_insets';

function applyInsets() {
  let i = window.__FZ_INSETS__;
  // Before MainActivity pushes fresh values (e.g. right after a reload), reuse
  // the last known ones so the bars don't jump under the status bar.
  if (!i) {
    try { i = JSON.parse(localStorage.getItem(INSETS_KEY) || 'null'); } catch { i = null; }
    if (i) i = { ...i, ime: false };
  } else {
    try { localStorage.setItem(INSETS_KEY, JSON.stringify(i)); } catch { /* storage blocked */ }
  }
  if (!i) return;
  const root = document.documentElement;
  root.style.setProperty('--native-safe-top', `${i.top}px`);
  root.style.setProperty('--native-safe-bottom', `${i.bottom}px`);
  root.style.setProperty('--native-safe-left', `${i.left}px`);
  root.style.setProperty('--native-safe-right', `${i.right}px`);
  root.classList.toggle('keyboard-open', !!i.ime);
}

async function listenForKeyboard() {
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    const root = document.documentElement;
    Keyboard.addListener('keyboardWillShow', () => root.classList.add('keyboard-open'));
    Keyboard.addListener('keyboardWillHide', () => root.classList.remove('keyboard-open'));
  } catch {
    /* plugin unavailable */
  }
}

async function configureNativeChrome() {
  if (!isNativeApp()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    // Android: MainActivity owns edge-to-edge and bar colours; the plugin's
    // overlay mode left a translucent grey band over the top bar on cold start.
    if (getNativePlatform() === 'android') return;
    await StatusBar.setBackgroundColor({ color: '#080414' });
    if (StatusBar.setOverlaysWebView) {
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
  } catch {
    /* plugin unavailable in browser */
  }
}

/**
 * Adds html.native-app classes and StatusBar overlay so CSS env(safe-area-*) owns insets.
 */
export function bootstrapNativeApp() {
  applyDocumentClasses();
  if (!isNativeApp()) return;
  applyInsets();
  window.addEventListener('fz-insets', applyInsets);
  listenForKeyboard();
  configureNativeChrome();
}
